import { setFocusToMonacoEditor } from './common/monaco-editor'

let pyodideWorker: Worker = null
let resolvePyodide: (value: unknown) => void = undefined
const interruptBuffer = new Uint8Array(new SharedArrayBuffer(1))
const sharedBuffer = new SharedArrayBuffer(256)
const signal = new Int32Array(sharedBuffer, 0, 1)
const inputQueue = new Uint8Array(sharedBuffer, 4)

const DEBUG = false

function log(...args: string[]) {
  if (DEBUG) {
    console.log('[api]:', ...args)
  }
}

function getPromiseAndResolve() {
  let futureResolve: (value: unknown) => void
  const promise = new Promise((resolve) => {
    futureResolve = resolve as (value: unknown) => void
  })
  return { promise, resolve: futureResolve }
}

const focusInputInstructionsId = 'focus-input-instructions'
const userInputContainerId = 'user-input-container'
const inputFieldId = 'python-input'
const inputPromptId = 'python-input-prompt'
const errorId = 'code-error'

export function hideInput() {
  const focusInputInstructions = document.getElementById(focusInputInstructionsId) as HTMLSpanElement
  const userInputContainer = document.getElementById(userInputContainerId) as HTMLDivElement
  const inputField = document.getElementById(inputFieldId) as HTMLInputElement
  const userInputPrompt = document.getElementById(inputPromptId) as HTMLSpanElement
  inputField.value = ''
  userInputContainer.style.display = 'none'
  focusInputInstructions.classList.add('hidden')
  userInputPrompt.innerHTML = ''
}

export function setInputEventListeners() {
  const inputField = document.getElementById(inputFieldId) as HTMLInputElement
  const userInput = inputField.value.trim()
  log('Got input:', userInput)

  const encodedInput = new TextEncoder().encode(userInput)

  inputQueue.fill(0)
  inputQueue.set(encodedInput, 4)
  Atomics.store(signal, 0, 1)
  // Unblock web worker
  Atomics.notify(signal, 0)
  hideInput()
  setFocusToMonacoEditor()
}

function requestUserInput(prompt?: string) {
  const inputField = document.getElementById(inputFieldId) as HTMLInputElement
  const focusInputInstructions = document.getElementById(focusInputInstructionsId) as HTMLSpanElement
  const userInputContainer = document.getElementById(userInputContainerId) as HTMLDivElement
  const userInputPrompt = document.getElementById(inputPromptId) as HTMLSpanElement

  const errorArea = document.getElementById(errorId) as HTMLDivElement

  function showInput(prompt: string) {
    userInputContainer.style.display = 'block'
    inputField.focus()
    focusInputInstructions.classList.remove('hidden')
    if (prompt.length) {
      userInputPrompt.innerHTML = prompt
    }
  }

  log('Waiting for user input...', prompt)
  showInput(prompt)
}

interface MainThreadMessageData {
  stdin?: string
  stdout?: string
  stderr?: string
  error?: string
  result?: string | undefined
}

function requestResponse(
  worker: Worker,
  stdoutFunc: (msg: string) => void,
  stderrFunc: (msg: string) => void,
  code: string,
) {
  const { promise, resolve } = getPromiseAndResolve()
  // Store globally to be used in cancellation
  resolvePyodide = resolve

  function listener(event: MessageEvent<MainThreadMessageData>) {
    if ('stdin' in event.data) {
      log('stdin requested', event.data.stdin)
      requestUserInput(event.data.stdin)
    } else if ('stderr' in event.data) {
      log('received stderr', event.data.stderr)
      stderrFunc(event.data.stderr)
    } else if ('error' in event.data) {
      log('received error', event.data.error)
      stderrFunc(event.data.error)
      cancelExecution()
    } else if ('stdout' in event.data) {
      log('received output', event.data.stdout)
      stdoutFunc(event.data.stdout)
    } else if ('result' in event.data) {
      log('Python execution completed', event.data.result)
      if (event.data.result !== undefined) {
        stdoutFunc(event.data.result)
      }
      resolve(event.data)
    } else {
      console.log('[api]: unknown event', event.data)
    }
  }

  // ensures the previous event listeners are cleaned up
  worker.onmessage = listener
  resetInterrupt()
  log('post message', code)
  worker.postMessage({ sharedBuffer, interruptBuffer, code })
  return promise
}

function setInterrupt() {
  const SIGINT = 2
  interruptBuffer[0] = SIGINT
}

function resetInterrupt() {
  const RESET = 0
  interruptBuffer[0] = RESET
}

export function startWorker() {
  if (!pyodideWorker) {
    pyodideWorker = new Worker('pyodide.worker.bundle.js')
  }
}

export function restartWorker() {
  if (pyodideWorker) {
    pyodideWorker.terminate()
    pyodideWorker = null
  }
  startWorker()
}

export function cancelExecution() {
  setInterrupt()
  // Interrupt does not always work. Let's kill the worker for now.
  // See: https://github.com/pyodide/pyodide/issues/5232
  restartWorker()
  hideInput()
  setFocusToMonacoEditor()
  if (resolvePyodide) {
    resolvePyodide('cancelled')
  }
}

export function asyncRun(code: string, stdoutFunc: (msg: string) => void, stderrFunc: (msg: string) => void) {
  startWorker()
  return requestResponse(pyodideWorker, stdoutFunc, stderrFunc, code)
}
